<template>
  <div>
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/tenants/payrollList" class="small d-inline-block mb-2">&larr; Payroll</router-link>
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Payroll ${obj.payrollNo || ""}` : "New payroll" }}
              <CBadge
                v-if="obj.statusDescription"
                :color="hrStatusColor('payroll', obj.statusDescription)"
                class="ml-2 align-middle"
              >
                {{ hrStatusLabel('payroll', obj.statusDescription) }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{ selectedEmployee && selectedEmployee.fullName ? selectedEmployee.fullName : "Choose an employee, then add earnings and deductions" }}
            </p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
            <CDropdown
              v-if="obj.id"
              placement="bottom-end"
              toggler-text="More"
              color="light"
              class="mr-2"
            >
              <CDropdownItem @click="addNew()">Add payroll</CDropdownItem>
              <template v-if="canGeneratePayslip">
                <CDropdownDivider />
                <CDropdownItem @click="generatePayslip()">
                  <CIcon name="cil-file" class="mr-1" /> Generate payslip
                </CDropdownItem>
              </template>
              <template v-if="computedPayrollStates.length">
                <CDropdownDivider />
                <CDropdownHeader>Change status</CDropdownHeader>
                <CDropdownItem
                  v-for="status in computedPayrollStates"
                  :key="status.id"
                  @click="changeState(status)"
                >
                  {{ stateActionLabel(status.name) }}
                </CDropdownItem>
              </template>
            </CDropdown>
            <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton
              v-if="isDraft"
              color="primary"
              :disabled="saving"
              @click="submit"
            >
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Create payroll" }}
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader><strong>Details</strong></CCardHeader>
          <CCardBody>
            <CForm>
              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Employee *
                </CCol>
                <CCol sm="9">
                  <v-select
                    style="width: 100%"
                    v-model="selectedEmployee"
                    :options="employeeItemsWithAddNew"
                    placeholder="Choose an employee"
                    @input="handleEmployeeSelect"
                    v-if="obj.statusDescription === 'Draft'"
                  >
                    <template #option="option">
                      <div v-if="option.isAddNewOption" class="font-weight-bold text-primary">
                        {{ option.fullName }}
                      </div>
                      <div v-else>
                        <div class="font-weight-bold">{{ option.fullName }}</div>
                        <small class="text-muted">{{ option.email }}</small>
                      </div>
                    </template>
                    <template #selected-option="option">
                      <span v-if="option.isAddNewOption">{{ option.fullName }}</span>
                      <span v-else>{{ option.fullName }} <small class="text-muted">({{ option.email }})</small></span>
                    </template>
                  </v-select>
                  <p v-else class="form-control-plaintext">{{ obj.profile && obj.profile.fullName }}</p>
                  <small v-if="employeeError" class="text-danger d-block mt-1">{{ employeeError }}</small>
                </CCol>
              </CRow>
              <CInput
                label="Payroll no."
                horizontal
                v-model="obj.payrollNo"
                :readonly="obj.statusDescription !== 'Draft'"
              />
              <CInput
                label="Reference"
                horizontal
                v-model="obj.reference"
                :readonly="obj.statusDescription !== 'Draft'"
              />

              <CInput
                type="date"
                :value="computeTransactionDate"
                @change="setTransactionDate"
                label="Date *"
                horizontal
                :readonly="obj.statusDescription !== 'Draft'"
              />

              <CInput
                type="date"
                :value="computePayPeriodStartDate"
                @change="setPayPeriodStartDate"
                label="Pay period from"
                horizontal
                :readonly="obj.statusDescription !== 'Draft'"
              />
              <CInput
                type="date"
                :value="computePayPeriodEndDate"
                @change="setPayPeriodEndDate"
                label="Pay period to"
                horizontal
                :readonly="obj.statusDescription !== 'Draft'"
              />

              <!-- Auto-calculate statutory contributions -->
              <CRow form class="form-group" v-if="selectedEmployee && obj.statusDescription === 'Draft'">
                <CCol tag="label" sm="3" class="col-form-label">
                  Statutory contributions
                </CCol>
                <CCol sm="9">
                  <CButton
                    color="primary"
                    variant="outline"
                    @click="calculateStatutory"
                    :disabled="calculatingStatutory || !selectedEmployee"
                  >
                    <CSpinner v-if="calculatingStatutory" size="sm" class="mr-1" />
                    <CIcon v-else name="cil-calculator" class="mr-1" />
                    {{ calculatingStatutory ? "Calculating…" : "Auto-calculate EPF, SOCSO and EIS" }}
                  </CButton>
                  <small class="form-text text-muted">
                    Uses this employee's gross pay and age to fill in the deductions and employer contributions below.
                  </small>
                  <small class="form-text text-muted" v-if="statutoryCalculationResult">
                    Last calculated {{ statutoryCalculationResult.timestamp }}
                    <span v-if="statutoryCalculationResult.grossSalary"> · gross pay {{ formatCurrency(statutoryCalculationResult.grossSalary) }}</span>
                  </small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader><strong>Earnings and deductions</strong></CCardHeader>
          <CCardBody>
            <CDataTable :items="obj.earnings" :fields="earningFields" responsive>
              <template #show_index="{ index }">
                <td class="py-2">
                  {{ index + 1 }}
                </td>
              </template>
              <template #show_type="{ item }">
                <td>
                  <CSelect
                    v-if="obj.statusDescription === 'Draft'"
                    horizontal
                    :value.sync="item.type"
                    :options="earningTypes"
                  />
                  <CInput
                    v-else
                    style="width: 100%"
                    :value="humanizeEnum(item.typeDescription)"
                    readonly
                  />
                </td>
              </template>
              <template #show_description="{ item }">
                <td>
                  <CInput
                    v-model="item.description"
                    :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>
              <template #show_account="{ item }">
                <td>
                  <CFormGroup>
                    <template #input>
                      <v-select
                        style="width: 100%"
                        v-model="item.chartAccount"
                        :label="`itemDisplay`"
                        :options="earningChartOfAccountItems"
                        placeholder="Choose an account"
                        v-if="obj.statusDescription === 'Draft'"
                      />
                      <CInput
                        v-else
                        style="width: 100%"
                        :value="item.chartAccount.itemDisplay"
                        readonly
                      />
                    </template>
                  </CFormGroup>
                </td>
              </template>

              <template #show_amount="{ item }">
                <td>
                  <CInput
                    v-model="item.amount"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    prepend="RM"
                    class="mb-0 text-right"
                    aria-label="Amount"
                    :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>

              <template #show_remove="{ item }">
                <td class="py-2">
                  <CButton
                    color="light"
                    @click="onRemoveEarning(item)"
                    v-if="obj.statusDescription === 'Draft'"
                    :aria-label="`Remove ${item.description || 'earning'}`"
                    title="Remove"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>

              <template #footer>
                <td colspan="3">
                  <CButton
                    @click="addNewEarning()" color="light"
                    v-if="obj.statusDescription === 'Draft'"
                  ><CIcon name="cil-plus" /> Add earning</CButton>
                </td>
                <td colspan="1" class="text-right">
                  <strong>Gross pay</strong>
                </td>
                <td class="text-right">{{ formatCurrency(grandTotalEarnings) }}</td>
                <td></td>
              </template>
            </CDataTable>

            <CDataTable :items="obj.deductions" :fields="deductionFields" responsive>
              <template #show_index="{ index }">
                <td class="py-2">
                  {{ index + 1 }}
                </td>
              </template>
              <template #show_type="{ item }">
                <td>
                  <CSelect
                  v-if="obj.statusDescription === 'Draft'"
                  horizontal
                    :value.sync="item.type"
                    :options="deductionTypes"
                  />
                  <CInput
                    v-else
                    style="width: 100%"
                    :value="humanizeEnum(item.typeDescription)"
                    readonly
                  />
                </td>
              </template>
              <template #show_description="{ item }">
                <td>
                  <CInput v-model="item.description"
                  :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>
              <template #show_account="{ item }">
                <td>
                  <CFormGroup>
                    <template #input>
                      <v-select
                        style="width: 100%"
                        v-model="item.chartAccount"
                        :label="`itemDisplay`"
                        :options="deductionChartOfAccountItems"
                        placeholder="Choose an account"
                        v-if="obj.statusDescription === 'Draft'"

                      />
                      <CInput
                        v-else
                        style="width: 100%"
                        :value="item.chartAccount.itemDisplay"
                        readonly
                      />
                    </template>
                  </CFormGroup>
                </td>
              </template>

              <template #show_amount="{ item }">
                <td>
                  <CInput
                    v-model="item.amount"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    prepend="RM"
                    class="mb-0 text-right"
                    aria-label="Amount"
                    :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>

              <template #show_remove="{ item }">
                <td class="py-2">
                  <CButton
                    color="light"
                    @click="onRemoveDeduction(item)"
                    v-if="obj.statusDescription === 'Draft'"
                    :aria-label="`Remove ${item.description || 'deduction'}`"
                    title="Remove"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>

              <template #footer>
                <tr>
                  <td colspan="3">
                    <CButton @click="addNewDeduction()" color="light"
                      v-if="obj.statusDescription === 'Draft'"
                    ><CIcon name="cil-plus" /> Add deduction</CButton>
                  </td>
                  <td colspan="1" class="text-right">
                    <strong>Deductions</strong>
                  </td>
                  <td class="text-right">{{ formatCurrency(grandTotalDeductions) }}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colspan="3"></td>
                  <td colspan="1" class="text-right">
                    <strong>Net pay</strong>
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCurrency(grandTotalEarnings - grandTotalDeductions) }}
                  </td>
                  <td></td>
                </tr>
              </template>
            </CDataTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader><strong>Employer contributions</strong></CCardHeader>
          <CCardBody>
            <CDataTable
              :items="obj.employerContributions"
              :fields="employerContributionFields"
              responsive
            >
              <template #show_index="{ index }">
                <td class="py-2">
                  {{ index + 1 }}
                </td>
              </template>
              <template #show_type="{ item }">
                <td>
                  <CSelect
                  v-if="obj.statusDescription === 'Draft'"
                  horizontal
                    :value.sync="item.type"
                    :options="employerContributionTypes"
                  />
                  <CInput
                    v-else
                    style="width: 100%"
                    :value="humanizeEnum(item.typeDescription)"
                    readonly
                  />
                </td>
              </template>
              <template #show_description="{ item }">
                <td>
                  <CInput v-model="item.description"
                  :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>
              <template #show_account="{ item }">
                <td>
                  <CFormGroup>
                    <template #input>
                      <v-select
                        style="width: 100%"
                        v-model="item.chartAccount"
                        :label="`itemDisplay`"
                        :options="employerContributionChartOfAccountItems"
                        placeholder="Choose an account"
                        v-if="obj.statusDescription === 'Draft'"

                      />
                      <CInput
                        v-else
                        style="width: 100%"
                        :value="item.chartAccount.itemDisplay"
                        readonly
                      />
                    </template>
                  </CFormGroup>
                </td>
              </template>
              <template #show_amount="{ item }">
                <td>
                  <CInput
                    v-model="item.amount"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    prepend="RM"
                    class="mb-0 text-right"
                    aria-label="Amount"
                    :readonly="obj.statusDescription !== 'Draft'"
                  ></CInput>
                </td>
              </template>

              <template #show_remove="{ item }">
                <td class="py-2">
                  <CButton
                    color="light"
                    @click="onRemoveEmployerContribution(item)"
                    v-if="obj.statusDescription === 'Draft'"
                    :aria-label="`Remove ${item.description || 'employer contribution'}`"
                    title="Remove"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>

              <template #footer>
                <td colspan="3">
                  <CButton @click="addNewEmployerContribution()" color="light"
                    v-if="obj.statusDescription === 'Draft'"
                  ><CIcon name="cil-plus" /> Add employer contribution</CButton>
                </td>
                <td colspan="1" class="text-right">
                  <strong>Total contributions</strong>
                </td>
                <td class="text-right">{{ formatCurrency(grandTotalContributions) }}</td>
                <td></td>
              </template>
            </CDataTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader><strong>Statutory contributions</strong></CCardHeader>
          <CCardBody>
            <div class="table-responsive"><table class="table table-bordered mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-right">Employee</th>
                  <th class="text-right">Employer</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in statutoryContributionsSummary" :key="item.type">
                  <td>
                    <strong>{{ item.type }}</strong>
                    <HrGlossaryTip :term="item.type" />
                  </td>
                  <td class="text-right">{{ formatCurrency(item.employeeAmount) }}</td>
                  <td class="text-right">{{ formatCurrency(item.employerAmount) }}</td>
                  <td class="text-right">{{ formatCurrency(item.totalAmount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-active">
                  <td><strong>Total</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(totalEmployeeStatutory) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(totalEmployerStatutory) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(totalStatutory) }}</strong></td>
                </tr>
              </tfoot>
            </table></div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Preview Section -->
    <CRow v-if="obj.id">
      <CCol lg="8">
        <CCard class="mb-4">
          <CCardHeader>
            <strong>Payslip preview</strong>
          </CCardHeader>
          <CCardBody class="p-0">
            <div class="payslip-preview">
              <WidgetsReportPayslip :payslip="computedPreviewPayslip" />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Sidebar -->
      <CCol lg="4">
        <PayslipSummaryComponent
          :grossIncome="grandTotalEarnings"
          :totalDeductions="grandTotalDeductions"
          :netPay="grandTotalEarnings - grandTotalDeductions"
          :totalEmployerContributions="grandTotalContributions"
          :statutorySummary="statutoryContributionsSummary"
          :currency="getCurrency()"
        />
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <div class="d-flex justify-content-end">
              <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">
                Cancel
              </CButton>
              <CButton v-if="isDraft" color="primary" :disabled="saving" @click="submit">
                <CSpinner v-if="saving" size="sm" class="mr-1" />
                {{ obj.id ? "Save" : "Create payroll" }}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CModal
      :title="`Mark payroll ${obj.payrollNo || ''} as paid?`"
      :show.sync="chooseAccountPopup"
      :close-on-backdrop="!changingState"
      @update:show="onAccountPopupConfirmation"
    >
      <p>
        Net pay of {{ formatCurrency(grandTotalEarnings - grandTotalDeductions) }}
        <span v-if="selectedEmployee && selectedEmployee.fullName">to {{ selectedEmployee.fullName }}</span>
        will be recorded as paid from the account you choose.
      </p>
      <CRow form>
        <CCol md="12">
          <label class="d-block">Paid from account *</label>
          <CFormGroup>
            <template #input>
              <v-select
                style="width: 100%"
                v-model="selectedChartAccount"
                :label="`itemDisplay`"
                :options="filteredChartAccounts"
                placeholder="Choose an account"
              />
            </template>
          </CFormGroup>
        </CCol>
      </CRow>

      <template #footer>
        <CButton color="light" :disabled="changingState" @click="chooseAccountPopup = false"
          >Cancel</CButton
        >
        <CButton
          color="primary"
          :disabled="!selectedChartAccount || changingState"
          @click="confirmAccountSelection"
        >
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          Mark as paid
        </CButton>
      </template>
    </CModal>

    <!-- Status Change Confirmation (approving posts to the ledger) -->
    <CModal
      :title="statusConfirmTitle"
      :show.sync="statusConfirmModal"
      :close-on-backdrop="!changingState"
      @update:show="onStatusConfirmModalClose"
    >
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="cancelStatusChange">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="changingState" @click="confirmStatusChange">
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ statusConfirmButton }}
        </CButton>
      </template>
    </CModal>

    <!-- Generate Payslip Confirmation -->
    <CModal
      :title="payslipConfirmTitle"
      :show.sync="payslipConfirmModal"
      :close-on-backdrop="!generatingPayslip"
    >
      <p class="mb-0">
        A payslip for {{ payslipEmployeeName }} will be created from payroll
        {{ obj.payrollNo }}, with year-to-date totals.
      </p>
      <template #footer>
        <CButton color="light" :disabled="generatingPayslip" @click="payslipConfirmModal = false">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="generatingPayslip" @click="doGeneratePayslip">
          <CSpinner v-if="generatingPayslip" size="sm" class="mr-1" />
          Generate payslip
        </CButton>
      </template>
    </CModal>

    <!-- Employee Quick-Add Modal -->
    <EmployeeFormModal
      :show="addEmployeeFormPopup"
      :employee="itemAddNewEmployee"
      @close="addEmployeeFormPopup = false"
      @save="onEmployeePopupConfirmation"
    />
  </div>
</template>

<script>
import PayrollApi from "@/lib/payrollApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import ProfileApi from "../../lib/profileApi";
import PaySlipApi from "../../lib/paySlipApi";
import PayrollCalculator from "../../utils/payrollCalculator";
import { formatDateTime, asDateString, mytToday } from "@/utils/datetime";
import { hrStatusMixin, hrStatusLabel } from "@/utils/hrStatusBadges";
import HrGlossaryTip from "../components/HrGlossaryTip";
import { formatMyr } from "@/utils/money";
import WidgetsReportPayslip from "../widgets/WidgetsReportPayslip";
import PayslipSummaryComponent from "../components/PayslipSummaryComponent";
import EmployeeFormModal from "../components/EmployeeFormModal";

// const claimItems = [];
// const claimFields = [
//   {
//     key: "show_index",
//     label: "#",
//     _style: "width:1%",
//     sorter: false,
//     filter: false,
//   },
//   {
//     key: "dateDisplay",
//     label: "Date",
//   },
//   {
//     key: "show_item_description",
//     label: "Item",
//   },
//   {
//     key: "show_receipt",
//     label: "Receipt",
//     _style: "width:160px",
//   },
//   {
//     key: "show_account",
//     label: "Account",
//     _style: "width:300px",
//   },
//   {
//     key: "show_total",
//     label: "Total",
//   },
// ];

export default {
  name: "Payroll",
  components: {
    vSelect,
    WidgetsReportPayslip,
    PayslipSummaryComponent,
    EmployeeFormModal,
    HrGlossaryTip,
  },
  mixins: [hrStatusMixin],
  data: () => {
    return {
      // earnings: [],
      employerContributionFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        {
          key: "show_type",
          label: "Type",
        },
        {
          key: "show_description",
          label: "Description",
        },
        {
          key: "show_amount",
          label: "Amount",
          _style: "width:200px; text-align:right",
        },
        {
          key: "show_remove",
          label: "",
          _style: "width:1%",
        },
      ],
      earningFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        {
          key: "show_type",
          label: "Type",
        },
        {
          key: "show_description",
          label: "Description",
        },
        {
          key: "show_amount",
          label: "Amount",
          _style: "width:200px; text-align:right",
        },
        {
          key: "show_remove",
          label: "",
          _style: "width:1%",
        },
      ],
      deductionFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        {
          key: "show_type",
          label: "Type",
        },
        {
          key: "show_description",
          label: "Description",
        },
        {
          key: "show_amount",
          label: "Amount",
          _style: "width:200px; text-align:right",
        },
        {
          key: "show_remove",
          label: "",
          _style: "width:1%",
        },
      ],
      profileApi: new ProfileApi(),

      selectedEmployee: null,
      employeeItems: [],
      addEmployeeFormPopup: false,
      itemAddNewEmployee: {},

      chooseAccountPopup: false,
      selectedChartAccount: null,
      pendingStatusChange: null,
      filteredChartAccounts: [],
      statusConfirmModal: false,
      changingState: false,
      payslipConfirmModal: false,
      generatingPayslip: false,

      chartOfAccountItems: [],
      expenseChartOfAccountItems: [],
      chartOfAccountApi: new ChartOfAccountApi(),
      helper,
      // Quotation Itm
      // claimItems: claimItems.map((item, id) => {
      //   return { ...item, id };
      // }),

      // claimFields,

      payrollStates: [],
      // selectedClaimState: null,
      organizationTypeList: [],
      saving: false,
      employeeError: "",
      obj: {
        profile: {
          fullName: "",
        },
        business: {
          name: "",
        },
      },
      submitted: false,
      api: new PayrollApi(),
      payrollCalculator: new PayrollCalculator(),
      calculatingStatutory: false,
      statutoryCalculationResult: null,
      earningTypes: [],
      deductionTypes: [],
      employerContributionTypes: [],
    };
  },
  mounted() {
    var self = this;
    this.fetchChartOfAccount();
    this.fetchPayrollStates();
    this.fetchEarningTypes();
    this.fetchDeductionTypes();
    this.fetchEmployerContributionTypes();
    this.refreshEmployee();
    // self.resetObj();
  },
  computed: {
    employerContributionChartOfAccountItems() {
      return this.chartOfAccountItems.filter(
        (a) => a.accountTypeDescription === "Expense"
      );
    },
    earningChartOfAccountItems() {
      return this.chartOfAccountItems.filter(
        (a) => a.accountTypeDescription === "Expense"
      );
    },
    deductionChartOfAccountItems() {
      return this.chartOfAccountItems.filter(
        (a) => a.accountTypeDescription === "Liability"
      );
    },
    employeeItemsWithAddNew() {
      return [
        {
          id: 'add_new',
          fullName: '+ Add employee',
          email: '',
          isAddNewOption: true
        },
        ...this.employeeItems
      ];
    },
    computePayPeriodStartDate() {
      return asDateString(this.obj.payPeriodStart);
    },
    computePayPeriodEndDate() {
      return asDateString(this.obj.payPeriodEnd);
    },
    computeTransactionDate() {
      return asDateString(this.obj.date);
    },
    isDraft() {
      return !this.obj.statusDescription || this.obj.statusDescription === "Draft";
    },
    computedPayrollStates() {
      let statuses = this.payrollStates.filter(
        (status) => status.name != this.obj.statusDescription
      );

      if (this.obj.id == null) return [];

      if (this.obj.statusDescription === "Draft")
        return statuses.filter((status) => status.name == "Approve");
      // if (this.obj.statusDescription === "Submit")
      //   return statuses.filter(
      //     (status) => status.name == "Approve" || status.name == "NotApprove"
      //   );
      if (this.obj.statusDescription === "Approve")
        return statuses.filter(
          (status) => status.name == "Paid" || status.name == "Cancelled"
        );
      else return [];
    },

    // computedChartAccountItems() {
    //   return this.chartOfAccountItems.filter(
    //     (a) => a.accountTypeDescription === "Expense"
    //   );

    //   // return this.chartOfAccountItems.map((item) => {
    //   //   return {
    //   //     ...item,
    //   //   };
    //   // });
    // },
    // computedEarnings() {
    //   return this.obj.earnings.map((item) => {
    //     return {
    //       ...item,
    //       // dateDisplay: helper.getDisplayDate(item.date),
    //       // productName: item.product.name,
    //       // totalAmountPerItem: this.getTotalItemPrice(item),
    //     };
    //   });
    // },
    payslipEmployeeName() {
      return (this.selectedEmployee && this.selectedEmployee.fullName) || "this employee";
    },
    payslipConfirmTitle() {
      return `Generate payslip for ${this.payslipEmployeeName}?`;
    },
    isPendingApprove() {
      return !!this.pendingStatusChange && this.pendingStatusChange.name === "Approve";
    },
    statusConfirmTitle() {
      const ref = this.obj.payrollNo ? ` ${this.obj.payrollNo}` : "";
      if (this.isPendingApprove) return `Approve payroll${ref}?`;
      const name = this.pendingStatusChange ? hrStatusLabel("payroll", this.pendingStatusChange.name).toLowerCase() : "";
      return `Mark payroll${ref} as ${name}?`;
    },
    statusConfirmMessage() {
      const ref = this.obj.payrollNo ? ` ${this.obj.payrollNo}` : "";
      const who = this.selectedEmployee && this.selectedEmployee.fullName
        ? ` for ${this.selectedEmployee.fullName}`
        : "";
      const amount = `RM ${formatMyr(this.grandTotalEarnings - this.grandTotalDeductions)}`;
      if (this.isPendingApprove)
        return `Payroll${ref}${who} (net pay ${amount}) will be approved and posted to your accounts.`;
      const name = this.pendingStatusChange ? hrStatusLabel("payroll", this.pendingStatusChange.name).toLowerCase() : "";
      if (name === "cancelled")
        return `Payroll${ref}${who} (net pay ${amount}) will be cancelled. It can't be paid after this.`;
      return `Payroll${ref}${who} (net pay ${amount}) will be marked as ${name}.`;
    },
    statusConfirmButton() {
      if (this.isPendingApprove) return "Approve payroll";
      if (this.pendingStatusChange && this.pendingStatusChange.name === "Cancelled") return "Cancel payroll";
      return "Change status";
    },

    grandTotalContributions() {
      var total = 0;
      for (var i = 0; i < this.obj.employerContributions.length; i++) {
        var item = this.obj.employerContributions[i];
        total += (parseFloat(item.amount) || 0);
      }
      return total;
    },
    grandTotalDeductions() {
      var total = 0;
      for (var i = 0; i < this.obj.deductions.length; i++) {
        var item = this.obj.deductions[i];
        total += (parseFloat(item.amount) || 0);
      }
      return total;
    },
    grandTotalEarnings() {
      var total = 0;
      for (var i = 0; i < this.obj.earnings.length; i++) {
        var item = this.obj.earnings[i];
        total += (parseFloat(item.amount) || 0);
      }
      return total;
    },
    // Statutory Contributions Summary
    statutoryContributionsSummary() {
      const summary = [
        {
          type: 'EPF',
          typeValue: 0,
          employeeAmount: 0,
          employerAmount: 0,
        },
        {
          type: 'SOCSO',
          typeValue: 1,
          employeeAmount: 0,
          employerAmount: 0,
        },
        {
          type: 'EIS',
          typeValue: 2,
          employeeAmount: 0,
          employerAmount: 0,
        },
      ];

      // SKBBK / LINDUNG 24 Jam is employee-borne only and opt-in — show it here only
      // when the employee is enrolled (i.e. a SKBBK deduction was calculated).
      const hasSkbbk = this.obj.deductions.some(
        (d) => d.type === 5 && (parseFloat(d.amount) || 0) > 0
      );
      if (hasSkbbk) {
        summary.push({
          type: 'SKBBK (LINDUNG 24 Jam)',
          typeValue: 5,
          employeeAmount: 0,
          employerAmount: 0,
        });
      }

      // Sum employee deductions by type
      this.obj.deductions.forEach((deduction) => {
        const summaryItem = summary.find((s) => s.typeValue === deduction.type);
        if (summaryItem) {
          summaryItem.employeeAmount += (parseFloat(deduction.amount) || 0);
        }
      });

      // Sum employer contributions by type
      this.obj.employerContributions.forEach((contribution) => {
        const summaryItem = summary.find((s) => s.typeValue === contribution.type);
        if (summaryItem) {
          summaryItem.employerAmount += (parseFloat(contribution.amount) || 0);
        }
      });

      // Add total amount for each row
      summary.forEach((item) => {
        item.totalAmount = item.employeeAmount + item.employerAmount;
      });

      return summary;
    },
    totalEmployeeStatutory() {
      return this.statutoryContributionsSummary.reduce((sum, item) => sum + item.employeeAmount, 0);
    },
    totalEmployerStatutory() {
      return this.statutoryContributionsSummary.reduce((sum, item) => sum + item.employerAmount, 0);
    },
    totalStatutory() {
      return this.totalEmployeeStatutory + this.totalEmployerStatutory;
    },
    // Preview computed property
    computedPreviewPayslip() {
      return {
        ...this.obj,
        business: auth.getLocalBusiness(),
      };
    },
    // Check if payslip can be generated
    canGeneratePayslip() {
      return this.obj.id &&
             (this.obj.statusDescription === "Approve" || this.obj.statusDescription === "Paid");
    },
  },
  watch: {
    selectedEmployee(newEmployee, oldEmployee) {
      if (newEmployee) this.employeeError = "";
      if (newEmployee && newEmployee !== oldEmployee) {
        // Auto-add Basic Salary earning if earnings table is empty
        if (this.obj.earnings.length === 0 && newEmployee.basicSalary) {
          // Find or auto-select an expense account for basic salary
          const expenseAccounts = this.earningChartOfAccountItems;
          let defaultAccount = null;

          if (expenseAccounts && expenseAccounts.length > 0) {
            // Try to find "Salary" or "Payroll" account
            defaultAccount = expenseAccounts.find(
              (acc) => acc.name.toLowerCase().includes('salary') ||
                      acc.name.toLowerCase().includes('payroll')
            ) || expenseAccounts[0]; // Fallback to first expense account
          }

          // Add Basic Salary earning
          this.obj.earnings.push({
            id: this.generateGUID(),
            type: 0, // PayrollEarningType.Basic = 0
            typeDescription: "Basic",
            description: "Basic Salary",
            amount: parseFloat(newEmployee.basicSalary),
            chartAccount: defaultAccount,
          });

          this.toast(
            "Info",
            `Basic salary of RM ${formatMyr(newEmployee.basicSalary)} added`,
            "info"
          );
        }
      }
    },
  },
  methods: {
    getCurrency() {
      var business = auth.getLocalBusiness();
      return business?.currency || 'MYR';
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    // "EpfEmployee" -> "EPF employee"; enum names are never shown raw.
    humanizeEnum(name) {
      if (!name) return "";
      const words = String(name)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .replace(/(epf|socso|eis|pcb|skbbk|hrdf)/g, (w) => w.toUpperCase());
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    stateActionLabel(name) {
      if (name === "Approve") return "Approve";
      if (name === "Paid") return "Mark as paid";
      if (name === "Cancelled") return "Cancel payroll";
      return hrStatusLabel("payroll", name);
    },
    async generatePayslip() {
      var self = this;

      if (!self.obj.id) {
        self.toast("Warning", "Save the payroll first, then generate the payslip", "warning");
        return;
      }

      if (!self.canGeneratePayslip) {
        self.toast("Warning", "Approve the payroll before generating a payslip", "warning");
        return;
      }

      self.payslipConfirmModal = true;
    },
    async doGeneratePayslip() {
      var self = this;
      self.generatingPayslip = true;
      try {
        const payslipApi = new PaySlipApi();
        const response = await payslipApi.generateFromPayroll(self.obj.id);
        const newPayslip = response.result;

        self.toast("Success", `Payslip ${newPayslip.no} generated`, "success");

        // Navigate to the new payslip
        self.$router.push({
          path: `/tenants/payslip/${newPayslip.id}`,
        });
      } catch (error) {
        console.error("Failed to generate payslip:", error);
        self.toast("Error", `Couldn't generate the payslip. ${helper.getErrorMessage(error && error.data)}`, "danger");
      } finally {
        self.generatingPayslip = false;
        self.payslipConfirmModal = false;
      }
    },
    setPayPeriodStartDate(e) {
      this.obj.payPeriodStart = e; // ISO format assumes local time
    },
    setPayPeriodEndDate(e) {
      this.obj.payPeriodEnd = e; // ISO format assumes local time
    },
    setTransactionDate(e) {
      this.obj.date = e; // ISO format assumes local time
    },
    refreshEmployee() {
      var self = this;
      self.profileApi
        .getListByCurrentBusiness(true)
        .then((response) => {
          self.employeeItems = response.result;
          self.resetObj();
        })
        .catch(({ data }) => {});
    },
    handleEmployeeSelect(selected) {
      if (selected?.id === 'add_new') {
        this.addNewEmployee();
        this.selectedEmployee = null;
      } else {
        this.selectedEmployee = selected;
      }
    },
    addNewEmployee() {
      this.itemAddNewEmployee = {
        fullName: '',
        email: '',
        dateOfBirth: null,
        basicSalary: null,
        epfNumber: '',
        socsoNumber: '',
        taxNumber: '',
        bankName: '',
        bankAccountName: '',
        bankAccountNumber: '',
        joinDate: mytToday(),
        employmentStatus: 'Active',
        isEnable: true,
        isEPFOptedOut: false,
        appUser: {
          role: 'Staff'
        }
      };
      this.addEmployeeFormPopup = true;
    },
    onEmployeePopupConfirmation(employeeData) {
      if (employeeData) {
        const self = this;
        // employeeData already contains profile, isResetPassword, plainPassword, and role from the modal
        self.profileApi
          .createProfileEmployee(employeeData)
          .then((response) => {
            const addedEmployee = response.result;
            self.refreshEmployee(); // Refresh employee list
            self.selectedEmployee = addedEmployee; // Auto-select new employee
            self.toast("Success", "Employee added", "success");
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't add the employee. ${helper.getErrorMessage(data)}`, "danger");
          });
      }
      this.addEmployeeFormPopup = false;
      this.itemAddNewEmployee = {};
    },
    changeState(item) {
      this.pendingStatusChange = item;
      this.selectedChartAccount = null; // Reset selected chart account
      if (item.name === "Paid") {
        this.filteredChartAccounts = this.chartOfAccountItems.filter(
          (a) => a.accountTypeDescription === "Asset"
        );
        // 🔍 Check historical expense data for this vendor
        this.api
          .getRecommendedChartAccount(this.obj.profileId, item.name)
          .then((response) => {
            const recommended = this.filteredChartAccounts.find(
              (a) => a.id === response.result
            );
            if (recommended) {
              this.selectedChartAccount = recommended;
            }
          })
          .finally(() => {
            this.chooseAccountPopup = true;
          });
        // this.chooseAccountPopup = true;
      } else {
        // Approving posts to the ledger, so ask first.
        if (!this.obj.id) {
          this.$toast.warning("Save the payroll before changing its status");
          this.pendingStatusChange = null;
          return;
        }
        this.statusConfirmModal = true;
      }
    },
    confirmStatusChange() {
      this.proceedChangeState(null);
    },
    cancelStatusChange() {
      this.statusConfirmModal = false;
      this.pendingStatusChange = null;
    },
    onStatusConfirmModalClose(visible) {
      if (!visible && !this.changingState) this.pendingStatusChange = null;
    },
    onAccountPopupConfirmation(visible) {
      this.chooseAccountPopup = visible;
    },

    confirmAccountSelection() {
      this.proceedChangeState(this.selectedChartAccount);
      this.selectedChartAccountId = null;
    },

    proceedChangeState(account) {
      var self = this;
      self.obj.status = self.pendingStatusChange.id;
      let accountId = null;
      if (account != null) accountId = account.id;

      if (self.obj.id) {
        self.changingState = true;
        this.api
          .updateState(self.obj, accountId)
          .then((response) => {
            self.toast("Success", `Payroll ${hrStatusLabel("payroll", self.pendingStatusChange && self.pendingStatusChange.name).toLowerCase()}`, "success");
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't change the payroll status. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            this.chooseAccountPopup = false;
            this.statusConfirmModal = false;
            this.changingState = false;
            this.pendingStatusChange = null;
          });
      }
    },
    cancel() {
      this.$router.push({ path: "/tenants/payrollList" });
    },

    // NEW: Calculate statutory contributions (EPF, SOCSO, EIS)
    async calculateStatutory() {
      const self = this;

      if (!self.selectedEmployee) {
        self.toast("Warning", "Choose an employee first", "warning");
        return;
      }

      // Check if employee has required data
      if (!self.selectedEmployee.basicSalary) {
        self.toast(
          "Error",
          "Add a basic salary to this employee's profile, then try again",
          "warning"
        );
        return;
      }

      if (!self.selectedEmployee.dateOfBirth) {
        self.toast(
          "Warning",
          "This employee has no date of birth, so age-based rates assume under 60",
          "warning"
        );
      }

      self.calculatingStatutory = true;

      try {
        // Calculate employee age
        const age = self.selectedEmployee.dateOfBirth
          ? self.payrollCalculator.calculateAge(self.selectedEmployee.dateOfBirth)
          : 30; // Default age if not provided

        // Calculate gross income from existing earnings
        const grossIncome = self.payrollCalculator.calculateGrossIncome(
          self.obj.earnings
        );
        const monthlySalary = grossIncome > 0 ? grossIncome : self.selectedEmployee.basicSalary;

        // Prepare calculation request
        const calculationData = {
          monthlySalary: monthlySalary,
          employeeAge: age,
          isEPFOptedOut: self.selectedEmployee.isEPFOptedOut || false,
          calculatePCB: false, // Don't calculate PCB by default
          taxReliefId: null,
          skbbkEnrolled: self.selectedEmployee.skbbkEnrolled || false, // SKBBK / LINDUNG 24 Jam opt-in
        };

        // Call backend API for accurate calculations
        const response = await self.api.calculateStatutory(calculationData);

        // Backend returns: { result: { epfEmployee, epfEmployer, socsoEmployee, ... } }
        const result = response.result;

        // Store calculation result
        self.statutoryCalculationResult = {
          timestamp: formatDateTime(new Date()),
          grossSalary: monthlySalary,
          epfEmployee: result.epfEmployee,
          epfEmployer: result.epfEmployer,
          socsoEmployee: result.socsoEmployee,
          socsoEmployer: result.socsoEmployer,
          eisEmployee: result.eisEmployee,
          eisEmployer: result.eisEmployer,
          skbbkEmployee: result.skbbkEmployee,
          totalDeductions: result.totalEmployeeDeductions,
          netPay: result.netPay,
        };

        // Add/Update EPF Employee Deduction (only if amount > 0)
        if (result.epfEmployee && result.epfEmployee > 0) {
          self.addOrUpdateDeduction(
            "EPF Employee",
            result.epfEmployee,
            0 // PayrollDeductionType.EpfEmployee = 0
          );
        }

        // Add/Update SOCSO Employee Deduction
        if (result.socsoEmployee && result.socsoEmployee > 0) {
          self.addOrUpdateDeduction(
            "SOCSO Employee",
            result.socsoEmployee,
            1 // PayrollDeductionType.SocsoEmployee = 1
          );
        }

        // Add/Update EIS Employee Deduction
        if (result.eisEmployee && result.eisEmployee > 0) {
          self.addOrUpdateDeduction(
            "EIS Employee",
            result.eisEmployee,
            2 // PayrollDeductionType.HrdfEmployee = 2 (or appropriate enum value)
          );
        }

        // Add/Update SKBBK / LINDUNG 24 Jam Deduction (employee-borne, opt-in)
        if (result.skbbkEmployee && result.skbbkEmployee > 0) {
          self.addOrUpdateDeduction(
            "LINDUNG 24 Jam (SKBBK)",
            result.skbbkEmployee,
            5 // PayrollDeductionType.SkbbkEmployee = 5
          );
        }

        // Add/Update EPF Employer Contribution
        if (result.epfEmployer && result.epfEmployer > 0) {
          self.addOrUpdateEmployerContribution(
            "EPF Employer",
            result.epfEmployer,
            0 // EmployerContributionType.EpfEmployer = 0
          );
        }

        // Add/Update SOCSO Employer Contribution
        if (result.socsoEmployer && result.socsoEmployer > 0) {
          self.addOrUpdateEmployerContribution(
            "SOCSO Employer",
            result.socsoEmployer,
            1 // EmployerContributionType.SocsoEmployer = 1
          );
        }

        // Add/Update EIS Employer Contribution
        if (result.eisEmployer && result.eisEmployer > 0) {
          self.addOrUpdateEmployerContribution(
            "EIS Employer",
            result.eisEmployer,
            2 // EmployerContributionType.HrdfEmployer = 2
          );
        }

        self.toast(
          "Success",
          "EPF, SOCSO and EIS calculated",
          "success"
        );
      } catch (error) {
        console.error("Statutory calculation error:", error);
        self.toast(
          "Error",
          "Couldn't calculate EPF, SOCSO and EIS. Try again.",
          "danger"
        );
      } finally {
        self.calculatingStatutory = false;
      }
    },

    // Helper: Add or update deduction
    addOrUpdateDeduction(description, amount, type) {
      const existing = this.obj.deductions.find((d) => d.description === description);

      if (existing) {
        // Update existing deduction
        existing.amount = amount;
      } else {
        // Auto-select a default chart account (Liability type for statutory deductions)
        // Prefer accounts with matching names (EPF Payable, SOCSO Payable, etc.)
        const liabilityAccounts = this.deductionChartOfAccountItems;
        let defaultAccount = null;

        if (liabilityAccounts && liabilityAccounts.length > 0) {
          // Try to find account with matching name
          const descLower = description.toLowerCase();
          defaultAccount = liabilityAccounts.find(
            (acc) => acc.name.toLowerCase().includes(descLower.split(' ')[0])
          ) || liabilityAccounts[0]; // Fallback to first liability account
        }

        // Add new deduction
        this.obj.deductions.push({
          id: this.generateGUID(),
          type: type,
          typeDescription: description,
          description: description,
          amount: amount,
          chartAccount: defaultAccount,
        });
      }
    },

    // Helper: Add or update employer contribution
    addOrUpdateEmployerContribution(description, amount, type) {
      const existing = this.obj.employerContributions.find(
        (c) => c.description === description
      );

      if (existing) {
        // Update existing contribution
        existing.amount = amount;
      } else {
        // Auto-select a default chart account (Expense type for employer contributions)
        const expenseAccounts = this.employerContributionChartOfAccountItems;
        let defaultAccount = null;

        if (expenseAccounts && expenseAccounts.length > 0) {
          // Try to find "Payroll Expense" or similar account
          defaultAccount = expenseAccounts.find(
            (acc) => acc.name.toLowerCase().includes('payroll')
          ) || expenseAccounts[0]; // Fallback to first expense account
        }

        // Add new contribution
        this.obj.employerContributions.push({
          id: this.generateGUID(),
          type: type,
          typeDescription: description,
          description: description,
          amount: amount,
          chartAccount: defaultAccount,
        });
      }
    },

    fetchChartOfAccount() {
      var self = this;
      self.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.chartOfAccountItems = response.result;
          self.expenseChartOfAccountItems = self.chartOfAccountItems.filter(
            (a) => a.accountTypeDescription === "Expense"
          );

          // self.resetObj();

          // self.chartAccountItemsLoaded = true;
        })
        .catch(({ data }) => {});
    },
    onRemoveEarning(item) {
      var self = this;
      if (self.obj.earnings != null) {
        for (var i = 0; i < self.obj.earnings.length; i++) {
          if (self.obj.earnings[i].id === item.id) {
            self.obj.earnings.splice(i, 1);
          }
        }
      }
    },
    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    addNewEmployerContribution() {
      this.obj.employerContributions.push({
        id: this.generateGUID(),
        type: 0,
        typeDescription: "Basic",
        description: "",
        amount: 0,
      });
    },
    onRemoveEmployerContribution(item) {
      var self = this;
      if (self.obj.employerContributions != null) {
        for (var i = 0; i < self.obj.employerContributions.length; i++) {
          if (self.obj.employerContributions[i].id === item.id) {
            self.obj.employerContributions.splice(i, 1);
          }
        }
      }
    },
    addNewDeduction() {
      this.obj.deductions.push({
        id: this.generateGUID(),
        type: 0,
        typeDescription: "Basic",
        description: "",
        amount: 0,
      });
    },
    onRemoveDeduction(item) {
      var self = this;
      if (self.obj.deductions != null) {
        for (var i = 0; i < self.obj.deductions.length; i++) {
          if (self.obj.deductions[i].id === item.id) {
            self.obj.deductions.splice(i, 1);
          }
        }
      }
    },
    addNewEarning() {
      this.obj.earnings.push({
        id: this.generateGUID(),
        type: 0,
        typeDescription: "Basic",
        description: "",
        amount: 0,
      });
    },
    getTotalItemPrice(item) {
      try {
        return item.quantity * item.amount;
      } catch (error) {
        return 0;
      }
    },
    fetchEarningTypes() {
      var self = this;
      self.api
        .getEarningTypes()
        .then((response) => {
          var obj = response.result;
          this.earningTypes = obj.map((state) => ({
            value: state.id,
            label: self.humanizeEnum(state.name),
          }));
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    fetchEmployerContributionTypes() {
      var self = this;
      self.api
        .getEmployerContributionTypes()
        .then((response) => {
          var obj = response.result;
          this.employerContributionTypes = obj.map((state) => ({
            value: state.id,
            label: self.humanizeEnum(state.name),
          }));
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    fetchDeductionTypes() {
      var self = this;
      self.api
        .getDeductionTypes()
        .then((response) => {
          var obj = response.result;
          this.deductionTypes = obj.map((state) => ({
            value: state.id,
            label: self.humanizeEnum(state.name),
          }));
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    fetchPayrollStates() {
      var self = this;
      self.api
        .getPayrollState()
        .then((response) => {
          this.payrollStates = response.result;
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            // self.claimItems = self.obj.items;
            // self.earnings = self.obj.earnings;
            self.selectedEmployee = self.obj.profile;
            // Set selectedClaimState to match the current state of the claim
            // self.selectedClaimState = self.obj.claimState;

            // var claimState ={
            //   value: self.obj.claimState,
            //   label: self.obj.claimStateDescription,
            // };
            // self.selectedClaimState = self.obj.claimState;

            // Handle duplication logic
            if (self.$route.name === "PayrollDuplicate") {
              delete self.obj.id;

              // Reset to current date
              self.obj.date = mytToday();

              // Get next payroll number and clear reference (will be generated by backend)
              self.api
                .getNextReferenceNumber()
                .then((response) => {
                  self.obj.payrollNo = response.result;
                })
                .catch(({ data }) => {});

              // Clear reference field to let backend generate a new one
              delete self.obj.reference;

              // Clear all audit fields from main object
              delete self.obj.createdAt;
              delete self.obj.updatedAt;
              delete self.obj.createdOn;
              delete self.obj.updatedOn;
              delete self.obj.creatorId;
              delete self.obj.creator;

              // Delete IDs and all audit fields from earnings
              if (self.obj.earnings && self.obj.earnings.length > 0) {
                for (var i = 0; i < self.obj.earnings.length; i++) {
                  delete self.obj.earnings[i].id;
                  delete self.obj.earnings[i].payrollId;
                  delete self.obj.earnings[i].createdAt;
                  delete self.obj.earnings[i].updatedAt;
                  delete self.obj.earnings[i].createdOn;
                  delete self.obj.earnings[i].updatedOn;
                  delete self.obj.earnings[i].creatorId;
                  delete self.obj.earnings[i].creator;
                }
              }

              // Delete IDs and all audit fields from deductions
              if (self.obj.deductions && self.obj.deductions.length > 0) {
                for (var i = 0; i < self.obj.deductions.length; i++) {
                  delete self.obj.deductions[i].id;
                  delete self.obj.deductions[i].payrollId;
                  delete self.obj.deductions[i].createdAt;
                  delete self.obj.deductions[i].updatedAt;
                  delete self.obj.deductions[i].createdOn;
                  delete self.obj.deductions[i].updatedOn;
                  delete self.obj.deductions[i].creatorId;
                  delete self.obj.deductions[i].creator;
                }
              }

              // Delete IDs and all audit fields from employer contributions
              if (self.obj.employerContributions && self.obj.employerContributions.length > 0) {
                for (var i = 0; i < self.obj.employerContributions.length; i++) {
                  delete self.obj.employerContributions[i].id;
                  delete self.obj.employerContributions[i].payrollId;
                  delete self.obj.employerContributions[i].createdAt;
                  delete self.obj.employerContributions[i].updatedAt;
                  delete self.obj.employerContributions[i].createdOn;
                  delete self.obj.employerContributions[i].updatedOn;
                  delete self.obj.employerContributions[i].creatorId;
                  delete self.obj.employerContributions[i].creator;
                }
              }

              // Reset status to Draft
              self.obj.status = 0;
              self.obj.statusDescription = "Draft";

              // Clear other tracking fields
              delete self.obj.expenseGenerated;
              delete self.obj.expenseId;

              self.toast("Info", "This is a copy. Check the details, then save", "info");
            }
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
        this.api
          .getNextReferenceNumber()
          .then((response) => {
            self.obj.payrollNo = response.result;
          })
          .catch(({ data }) => {});
      }
    },
    onSubmit() {
      var self = this;

      // let haveError = false;
      // self.computedClaimItems.forEach((item) => {
      //   if (!item.chartAccount || !item.chartAccount.id) {
      //     haveError = true;
      //   } else {
      //     item.chartAccountId = item.chartAccount.id;
      //   }
      // });

      // if (haveError) {
      //   self.toast(
      //     "Error",
      //     "Please select chart of account for all items",
      //     "danger"
      //   );
      //   return;
      // }

      // self.obj.claimState = self.selectedClaimState;
      // self.obj.items = self.computedClaimItems;

      if (self.selectedEmployee == null) {
        self.employeeError = "Choose the employee this payroll is for.";
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      self.employeeError = "";
      if (self.saving) return;
      // if (self.obj.earnings.length == 0) {
      //   self.toast("Error", "Please add at least one earning", "danger");
      //   return;
      // }
      
      self.obj.profile = self.selectedEmployee;
      self.obj.profileId = self.selectedEmployee.id;

      // Clean up navigation properties to prevent EF duplicate key errors
      // Keep only required fields for validation and foreign key relationships
      self.obj.business = null;
      if (self.obj.profile) {
        const profileId = self.obj.profile.id;
        const profileEmail = self.obj.profile.email;
        self.obj.profile = { id: profileId, email: profileEmail };
      }

      self.obj.earnings = self.obj.earnings.map((item) => {
        return {
          ...item,
          chartAccountId: item.chartAccount?.id || null,
          chartAccount: null, // Remove navigation property
          payroll: null, // Remove navigation property
        };
      });

      self.obj.deductions = self.obj.deductions.map((item) => {
        return {
          ...item,
          chartAccountId: item.chartAccount?.id || null,
          chartAccount: null, // Remove navigation property
          payroll: null, // Remove navigation property
        };
      });

      self.obj.employerContributions = self.obj.employerContributions.map(
        (item) => {
          return {
            ...item,
            chartAccountId: item.chartAccount?.id || null,
            chartAccount: null, // Remove navigation property
            payroll: null, // Remove navigation property
          };
        }
      );
      self.saving = true;
      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.toast("Success", "Payroll created", "success");
            var newObj = response.result;
            self.$router.push({
              path: `/tenants/payroll/${newObj.id}`,
            });

            // this.resetObj();
            // self.$router.push({ path: "/employee/claimList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't create the payroll. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            self.toast("Success", "Payroll saved", "success");
            this.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't save the payroll. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    onReset() {
      this.obj = {};
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },

    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const pad = (n) => n.toString().padStart(2, "0");
      const ref =
        "PAY-" +
        today.getFullYear() +
        pad(today.getMonth() + 1) +
        pad(today.getDate()) +
        "-" +
        pad(today.getHours()) +
        pad(today.getMinutes()) +
        pad(today.getSeconds());

      return {
        profile: {
          fullName: "",
        },
        reference: ref,
        payrollNo: "", // can use same or different logic
        date: today,
        payPeriodStart: startOfMonth,
        payPeriodEnd: endOfMonth,
        earnings: [],
        deductions: [],
        employerContributions: [],
        status: 0,
        statusDescription: "Draft",
      };
    },
    submit() {
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>

<style scoped>
.payslip-preview {
  transform: scale(0.8);
  transform-origin: top center;
  overflow: hidden;

}

@media (max-width: 767.98px) {
  .payslip-preview {
    transform: scale(0.7);
  }
}
</style>
